/* global Twilio */
import * as Sentry from '@sentry/react';
import { get } from 'lodash';
import {
  store,
  getAgentToken,
  getPendingTasks,
  getTaskInfo,
  setAvailability,
  logout,
  initializeWorker,
  workerReady,
  clearTask,
  startCall,
  completeCall,
  wrapTask,
  getCallStatus,
  getJobAgentSow
} from '../redux';
import getActiveJobSelector from '../redux/selectors/app/active-job';
import { ROUTES, CALL_STATUS, RECORDING_STATUS, TASK_STATUS } from '../../Constants';

let worker = null;
let currentReservation = null;

export function destroyWorker() {
  if (!worker) {
    return;
  }

  worker.update('ActivitySid', window._env_.TWILIO_OFFLINE_SID, updateError => {
    if (updateError) {
      // Can't have an empty catch block, so we just console.error it
      // eslint-disable-next-line no-console
      console.error('update worker err', updateError);
    }
    worker = null;
  });
}

function fetchAcceptedReservations() {
  return new Promise((resolve, reject) => {
    worker.fetchReservations(
      (error, reservations) => {
        if (error) {
          return reject(false);
        }

        return resolve(reservations.data);
      },
      { ReservationStatus: 'accepted' }
    );
  });
}

function redirectToFeedbackPage(history) {
  try {
    store.dispatch(workerReady());

    return history.push(ROUTES.JOB_FEEDBACK);
  } catch (err) {
    store.dispatch(workerReady());

    return history.push(ROUTES.JOB_FEEDBACK, { withError: true });
  }
}

function redirectToCallPage(history, params) {
  try {
    store.dispatch(workerReady());

    return history.push(ROUTES.JOB_CALL, params);
  } catch (err) {
    store.dispatch(workerReady());
    return history.push(ROUTES.JOB_CALL, { withError: true });
  }
}

async function setCurrentReservation() {
  const reservations = await fetchAcceptedReservations();

  if (reservations.length) {
    const [reservation] = reservations;
    currentReservation = reservation;

    if (reservations.length > 1) {
      const taskIds = reservations.map(r => get(r, 'task.attributes.taskId') || '').join();
      Sentry.captureException(new Error(`More than 1 reservations. TaskIds: ${taskIds}`));
    }
  } else {
    currentReservation = null;
  }
}

export function setUnavailable() {
  return new Promise((resolve, reject) => {
    if (!worker) {
      return resolve();
    }

    return worker.update('ActivitySid', window._env_.TWILIO_UNAVAILABLE_SID, error => {
      if (error) {
        return reject(error);
      }
      return resolve();
    });
  });
}

export function setAvailable() {
  return new Promise((resolve, reject) => {
    if (!worker) {
      return resolve();
    }

    return worker.update('ActivitySid', window._env_.TWILIO_AVAILABLE_SID, error => {
      if (error) {
        return reject(error);
      }
      return resolve();
    });
  });
}

function redirectToPage(history) {
  const { call } = store.getState();
  if (
    call.callStatus !== null &&
    (call.callStatus === CALL_STATUS.BUSY.name ||
      call.callStatus === CALL_STATUS.FAILED.name ||
      call.callStatus === CALL_STATUS.NOANSWER.name ||
      call.callStatus === CALL_STATUS.CANCELED.name ||
      call.callStatus === CALL_STATUS.COMPLETED.name)
  ) {
    return redirectToCallPage(history, { recordingStatus: RECORDING_STATUS.COMPLETED });
  }
  let redirectToCallPageParams = {};
  if (
    call.callStatus !== null &&
    (call.callStatus === CALL_STATUS.INITIATED.name || call.callStatus === CALL_STATUS.RINGING.name)
  ) {
    return redirectToCallPage(history, { recordingStatus: null });
  }
  if (call.callStatus !== null && call.callStatus === CALL_STATUS.INPROGRESS.name) {
    if (call.recordingStatus === RECORDING_STATUS.INPROGRESS) {
      redirectToCallPageParams = { recordingStatus: RECORDING_STATUS.INPROGRESS };
    } else if (
      call.recordingStatus === RECORDING_STATUS.ABSENT ||
      call.recordingStatus === RECORDING_STATUS.DELETED ||
      call.recordingStatus === RECORDING_STATUS.COMPLETED
    ) {
      redirectToCallPageParams = { recordingStatus: null };
    } else {
      redirectToCallPageParams = null;
    }
  }
  return redirectToCallPage(history, redirectToCallPageParams);
}

export function initWorker(history, callback) {
  store.dispatch(initializeWorker());

  const { agent } = store.getState();

  worker = new Twilio.TaskRouter.Worker(agent.token);

  worker.on('ready', async readyWorker => {
    // HACK: Attempt to force a worker as unavailable if we are not currently available.
    // This fixes a bug where we have an empty Twilio entry in our database for NEW agents
    // by changing their status from Offline -> Unavailable.
    // Will trigger a Twilio availability webhook.
    if (!readyWorker.available) {
      try {
        await setUnavailable();
      } catch (err) {
        console.error('Failed to force worker as unavailable');
        console.error(err);
      }
    }

    store.dispatch(setAvailability(readyWorker.available));

    try {
      await setCurrentReservation();

      if (currentReservation !== null) {
        await store.dispatch(getTaskInfo(currentReservation.task.attributes.taskId));

        const { tasks } = store.getState();

        // if is it a new call
        if (!tasks.currentTask.task.taskCallId) {
          await store.dispatch(
            startCall(currentReservation.task.attributes.taskId, tasks.currentTask.task.jobTypeCode)
          );
          return redirectToCallPage(history);
        }

        await store.dispatch(
          getCallStatus(
            currentReservation.task.attributes.taskId,
            tasks.currentTask.task.taskCallId
          )
        );

        return redirectToPage(history);
      }
    } catch (err) {
      // Can't have an empty catch block, so we just console.error it
      // eslint-disable-next-line no-console
      console.error(err);
    }

    const pendingTasks = await store.dispatch(getPendingTasks());

    if (pendingTasks.tasks.length) {
      store.dispatch(workerReady());

      const [task] = pendingTasks.tasks;
      if (task.taskStatus === TASK_STATUS.Wrapping.name) {
        await store.dispatch(getTaskInfo(task.taskId));
        return redirectToFeedbackPage(history);
      }
      return history.push(ROUTES.JOB_CALL);
    }

    store.dispatch(workerReady());

    if (readyWorker.available) {
      return history.push(ROUTES.JOB);
    }

    return callback();
  });

  worker.on('token.expired', async () => {
    try {
      const { token } = await store.dispatch(getAgentToken());
      worker.updateToken(token);
      await setCurrentReservation();
    } catch (err) {
      await store.dispatch(logout());
      destroyWorker();
      history.push(ROUTES.LOGIN);
    }
  });

  worker.on('activity.update', updatedWorker => {
    store.dispatch(setAvailability(updatedWorker.available));
  });

  worker.on('reservation.created', async reservation => {
    currentReservation = reservation;
    await store.dispatch(getTaskInfo(reservation.task.attributes.taskId));
    const state = store.getState();
    const activeJob = getActiveJobSelector(state);

    if (!state.me.agentIsVendor) {
      const { isSigned } = await store.dispatch(getJobAgentSow(activeJob.jobId));

      if (!isSigned) {
        return history.push(ROUTES.SOW, { nextRoute: ROUTES.JOB_INSTRUCTIONS });
      }
    }

    return history.push(ROUTES.JOB_INSTRUCTIONS);
  });

  worker.on('reservation.canceled', () => {
    currentReservation = null;
    store.dispatch(clearTask());
    history.push(ROUTES.JOB);
  });

  worker.on('reservation.timeout', () => {
    currentReservation = null;
    store.dispatch(clearTask());
    setUnavailable();
    history.push(ROUTES.HOME);
  });
}

export function acceptReservation() {
  // eslint-disable-next-line consistent-return
  return new Promise((resolve, reject) => {
    if (!currentReservation) {
      return resolve(false);
    }

    try {
      currentReservation.accept(error => {
        if (error) {
          return reject(error);
        }

        return resolve(true);
      });
    } catch (err) {
      return reject(err);
    }
  });
}

export function rejectReservation(setUnavailableNext = false, setOfflineNext = false) {
  // eslint-disable-next-line consistent-return
  return new Promise((resolve, reject) => {
    if (!currentReservation) {
      return resolve(false);
    }

    let nextStatus = window._env_.TWILIO_AVAILABLE_SID;

    if (setUnavailableNext) {
      nextStatus = window._env_.TWILIO_UNAVAILABLE_SID;
    } else if (setOfflineNext) {
      nextStatus = window._env_.TWILIO_OFFLINE_SID;
    }

    try {
      currentReservation.reject(nextStatus, error => {
        if (error) {
          return reject(error);
        }

        currentReservation = null;
        return resolve(true);
      });
    } catch (err) {
      return reject(err);
    }
  });
}

export function finishTask() {
  // eslint-disable-next-line consistent-return
  return new Promise((resolve, reject) => {
    if (!currentReservation) {
      return resolve(false);
    }

    try {
      store.dispatch(completeCall());
      currentReservation = null;
      return resolve(true);
    } catch (err) {
      return reject(err);
    }
  });
}

export function wrappingTask() {
  // eslint-disable-next-line consistent-return
  return new Promise((resolve, reject) => {
    if (!currentReservation) {
      return resolve(false);
    }

    try {
      currentReservation.task.update({ AssignmentStatus: 'wrapping' }, error => {
        if (error) {
          return reject(error);
        }

        store.dispatch(wrapTask());
        return resolve(true);
      });
    } catch (err) {
      return reject(err);
    }
  });
}

export function clearReservation() {
  currentReservation = null;
}
